import { ActionIcon, CloseButton, Group, Loader, Menu, ScrollArea, TextInput } from "@mantine/core";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowUpDown, Check, Search } from "lucide-react";
import { useState } from "react";

import {
  getListActorsQueryKey,
  listActors,
} from "../../../api/generated/endpoints/generation/generation";
import type { AiActor, AssetOrder, ListActorsParams } from "../../../api/generated/model";
import {
  NO_ACTOR_FILTER,
  facetParams,
  type ActorFilter,
} from "../../../platform/actors/ActorFacets";
import { videoItem } from "../../../platform/actors/actor-preview";
import { useCreateTalkingHead } from "../../../platform/actors/talking-head-handoff";
import { MediaPreview } from "../../../platform/media/MediaPreview";
import { readPref, writePref } from "../../../platform/prefs";
import { ColumnsSetting } from "../../../ui/ColumnsSetting";
import { EmptyState } from "../../../ui/EmptyState";
import { MasonryGrid } from "../../../ui/MasonryGrid";
import { ActorCard } from "./ActorCard";
import { ActorFilters } from "./ActorFilters";
import { LoadAhead, morePages } from "../../../ui/LoadAhead";
import classes from "./actors.module.css";

/** Live's page size (`constants/assets.tsx`), and its column counts (2 to 10 by two). */
const PAGE = 24;
const COLUMN_MARKS = [2, 4, 6, 8, 10] as const;

/** Live's sort (`constants/filters.ts`): newest first by default. */
const ORDERS: { value: AssetOrder; label: string }[] = [
  { value: "newest", label: "Recently Added" },
  { value: "oldest", label: "Oldest First" },
];

/** The route's parameters for a search, a filter and the order: empty ones left out, as live's. */
function paramsOf(search: string, filter: ActorFilter, order: AssetOrder): ListActorsParams {
  return {
    kind: "global",
    ...(order === "newest" ? {} : { order }),
    ...(search === "" ? {} : { search }),
    ...facetParams(filter),
  };
}

/**
 * What the page keeps across its tabs, as live's page context (`actors-ai-filters-context`):
 * the search (as typed, and as applied), the order, and the Global Actors' facets.
 */
export interface ActorsQuery {
  typed: string;
  search: string;
  order: AssetOrder;
  filter: ActorFilter;
}

export const NO_ACTORS_QUERY: ActorsQuery = {
  typed: "",
  search: "",
  order: "newest",
  filter: NO_ACTOR_FILTER,
};

/** Live's search box takes 100 characters at most, as the route does. */
const SEARCH_MAX = 100;

export interface GlobalActorsProps {
  teamId: string;
  brandId: string;
  /** The page's search, order and facets, kept while another tab shows. */
  query: ActorsQuery;
  onQuery: (change: Partial<ActorsQuery>) => void;
  /** "Create a Talking Head": the Studio's Talking Head unless the page opens its own. */
  onTalkingHead?: ((actor: AiActor) => void) | undefined;
  /** At most this many columns, whatever the saved setting (the redesign's page on a phone). */
  columnsAtMost?: number | undefined;
}

/**
 * Live's Global Actors tab (`tabs/global-actors`, captures 08 to 10): the search, applied on
 * Enter or its arrow, live's filter, live's sort (Recently Added, Oldest First) and the columns;
 * then live's 119 actors as live's masonry of cards, a page of 24 at a time. A card opens the
 * actor's video, titled with its name as live's preview, in the shared lightbox (issue 828, bug 25,
 * Daniil's request beyond live): the list's actors full screen, paging as the list does.
 */
export function GlobalActors(props: GlobalActorsProps) {
  const { teamId, brandId, query, onQuery, onTalkingHead, columnsAtMost } = props;
  const { typed, search, order, filter } = query;
  const [columns, setColumns] = useState(() => readPref("actorColumns"));
  // The actor the lightbox shows, by id; null while it is closed.
  const [previewing, setPreviewing] = useState<string | null>(null);
  const studioTalkingHead = useCreateTalkingHead();
  const createTalkingHead = onTalkingHead ?? studioTalkingHead;
  const params = paramsOf(search, filter, order);
  const actors = useInfiniteQuery({
    queryKey: [...getListActorsQueryKey(teamId, brandId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listActors(
        teamId,
        brandId,
        { ...params, limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const items = actors.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <div className={classes.tab}>
      <div className={classes.toolbar}>
        <Group gap="sm" wrap="nowrap">
          <TextInput
            className={classes.search}
            aria-label="Search by actor name or partial match"
            placeholder="Search by actor name or partial match"
            leftSection={<Search size={16} aria-hidden />}
            value={typed}
            maxLength={SEARCH_MAX}
            onChange={(event) => onQuery({ typed: event.currentTarget.value })}
            onKeyDown={(event) => {
              if (event.key === "Enter") onQuery({ search: typed.trim() });
            }}
            rightSectionWidth={typed === "" ? 36 : 64}
            rightSection={
              <Group gap={2} wrap="nowrap">
                {typed === "" ? null : (
                  <CloseButton
                    size="sm"
                    aria-label="Clear input"
                    onClick={() => {
                      onQuery({ typed: "", search: "" });
                    }}
                  />
                )}
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  aria-label="Search"
                  disabled={typed === "" && search === ""}
                  onClick={() => onQuery({ search: typed.trim() })}
                >
                  <ArrowRight size={16} />
                </ActionIcon>
              </Group>
            }
          />
          <ActorFilters applied={filter} onApply={(next) => onQuery({ filter: next })} />
        </Group>
        <Group gap={4} wrap="nowrap">
          <Menu position="bottom-end" withinPortal={false}>
            <Menu.Target>
              <ActionIcon variant="subtle" size="lg" aria-label="Sort">
                <ArrowUpDown size={20} strokeWidth={1.5} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {ORDERS.map((each) => (
                <Menu.Item
                  key={each.value}
                  rightSection={each.value === order ? <Check size={16} /> : null}
                  onClick={() => onQuery({ order: each.value })}
                >
                  {each.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <ColumnsSetting
            value={columns}
            marks={COLUMN_MARKS}
            onChange={(next) => {
              setColumns(next);
              writePref("actorColumns", next);
            }}
          />
        </Group>
      </div>
      {/* The list scrolls on its own under the toolbar, which stays (live's `TabLayout`). */}
      <ScrollArea type="auto" offsetScrollbars className={classes.list}>
        {actors.isPending ? <Loader size="sm" aria-label="Loading the actors" /> : null}
        {/* Live shows its empty words when the list fails too. */}
        {!actors.isPending && items.length === 0 ? (
          <EmptyState
            title="No global actors found"
            description="Check for typos or filters, or try a different search query."
          />
        ) : null}
        {items.length > 0 ? (
          <MasonryGrid columns={Math.min(columns, columnsAtMost ?? columns)} label="Global Actors">
            {items.map((actor) => (
              <ActorCard
                key={actor.id}
                actor={actor}
                onPreview={() => setPreviewing(actor.id)}
                onLost={() => void actors.refetch()}
                onTalkingHead={() => createTalkingHead(actor)}
              />
            ))}
          </MasonryGrid>
        ) : null}
        {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
        <LoadAhead more={morePages(actors)} loaded={items.length} label="Loading more actors" />
      </ScrollArea>
      <MediaPreview
        items={items.map(videoItem)}
        current={previewing}
        onShow={setPreviewing}
        more={morePages(actors)}
      />
    </div>
  );
}
